import React, { useEffect, useState } from "react";

// Animated number counter with ease-out
export const Counter = ({ value, duration = 1200, decimals = 0, className = "" }) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    const start = performance.now();
    const from = 0;
    const to = value;
    let raf;
    const step = (t) => {
      const e = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - e, 3);
      setN(from + (to - from) * eased);
      if (e < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return <span className={`font-mono tabular ${className}`}>{n.toFixed(decimals)}</span>;
};

// Circular SVG digital meter
export const CircularMeter = ({
  value = 0,
  size = 220,
  stroke = 6,
  label = "PROGRESS",
  suffix = "%",
}) => {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (value / 100) * c;

  return (
    <div className="relative inline-flex items-center justify-center" data-testid="circular-meter">
      <svg width={size} height={size} className="-rotate-90">
        <defs>
          <linearGradient id="goldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#B26A2F" />
            <stop offset="50%" stopColor="#DDA74F" />
            <stop offset="100%" stopColor="#F0C175" />
          </linearGradient>
          <filter id="goldGlow">
            <feGaussianBlur stdDeviation="3" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          style={{ stroke: "var(--meter-track-color)" }}
          strokeWidth={stroke}
          fill="none"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke="url(#goldGrad)"
          strokeWidth={stroke}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          filter="url(#goldGlow)"
          style={{ transition: "stroke-dashoffset 1.2s cubic-bezier(0.22,1,0.36,1)" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="text-[16px] tracking-widest-2 text-ink font-mono">{label}</div>
        <div className="font-mono text-4xl text-ink gold-glow tabular leading-none mt-1">
          <Counter value={value} />
          <span className="text-ink-2 text-xl ml-1">{suffix}</span>
        </div>
      </div>
    </div>
  );
};

// Linear shimmery meter
export const LinearMeter = ({ value = 0, label, sublabel }) => {
  return (
    <div className="w-full" data-testid="linear-meter">
      {label && (
        <div className="flex items-end justify-between mb-2">
          <div className="text-[16px] tracking-widest-2 uppercase text-ink font-mono">
            {label}
          </div>
          <div className="font-mono text-base text-gold tabular">
            <Counter value={value} />%
          </div>
        </div>
      )}
      <div className="meter-track">
        <div className="meter-fill" style={{ width: `${value}%` }} />
      </div>
      {sublabel && <div className="text-base text-ink mt-2 font-mono">{sublabel}</div>}
    </div>
  );
};

export default CircularMeter;
